import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import axios from 'axios';
import { Option } from 'types/general.types';
import { AuddlySignUpFormStep } from 'types/enums';
import { AuddlySignUpResponse } from 'types/auth';
import { useAppSelector } from 'app/store';
import {
  getPasswordPolicy,
  getPasswordResetLinkSelector,
} from 'app/store/Auth/selectors';
import { fetchCountriesEffect } from 'app/store/Countries/effects';
import { fetchPasswordPolicyEffect } from 'app/store/Auth/effects';
import { getCountries } from 'app/store/Countries/selectors';
import { toCountryOptions } from 'app/components/Profile/ProfilePersonalSection/utils';
import { auddlySignUpValidationSchema } from 'app/components/AuddlySignUp/utils';
import { AuddlySignUpFormStepIndicator } from 'app/components/AuddlySignUp/AuddlySignUpFormStepIndicator';
import { AuddlySignUpActions } from 'app/components/AuddlySignUp/AuddlySignUpActions';
import { AuddlySignUpPasswordForm } from 'app/components/AuddlySignUp/AuddlySignUpPasswordForm';
import { AuddlySignUpLocationForm } from 'app/components/AuddlySignUp/AuddlySignUpLocationForm';
import { routes } from 'app/routing/routesConfig';
import { useAuddlySignUpError } from 'app/components/AuddlySignUp/AuddlySignUpForm/hooks';
import { useResource } from 'app/utils/hooks';

interface AuddlySignUpFormProps {
  signUpLink?: string;
  afterSubmit: () => void;
}

export interface AuddlySignUpFormFields {
  email: string;
  code: string;
  password: string;
  passwordRepeat: string;
  city: string;
  country: Option | null;
  emailOptIn: boolean;
}

const defaultValues = {
  email: '',
  code: '',
  password: '',
  passwordRepeat: '',
  city: '',
  country: null,
  emailOptIn: false,
};

export const AuddlySignUpForm: React.FC<AuddlySignUpFormProps> = ({
  signUpLink,
  afterSubmit,
}) => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [codeErrorMessage, setCodeErrorMessage] = useState('');
  const { email = '', code = '' } = queryString.parse(search, { decode: true });

  const [passwordPolicy] = useResource(
    fetchPasswordPolicyEffect,
    getPasswordPolicy
  );
  const [countries] = useResource(fetchCountriesEffect, getCountries);

  const resendLink = useAppSelector(getPasswordResetLinkSelector);
  const countryOptions = toCountryOptions(countries);

  const [formStep, setFormStep] = useState<AuddlySignUpFormStep>(
    AuddlySignUpFormStep.PASSWORD_CREATION
  );

  const getDefaultFormValues = (email: string, code: string, values: any) => ({
    ...values,
    email,
    code,
  });

  const form = useForm<AuddlySignUpFormFields>({
    defaultValues: getDefaultFormValues(
      email as string,
      code as string,
      defaultValues
    ),
    mode: 'all',
    resolver: yupResolver(auddlySignUpValidationSchema(passwordPolicy)),
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting, errors },
    setError,
    getValues,
    watch,
  } = form;

  const watchEmail = watch('email');

  const { handleError } = useAuddlySignUpError({
    afterSubmit,
    setError,
    setCodeErrorMessage,
    setFormStep,
  });

  const onSubmit = async (values: AuddlySignUpFormFields) => {
    setCodeErrorMessage('');
    if (signUpLink) {
      try {
        await axios.post<AuddlySignUpResponse>(signUpLink, {
          email: values.email,
          code: values.code,
          newPassword: values.password,
          city: values.city,
          countryCode: values.country?.value,
          emailOptIn: values.emailOptIn,
        });
        afterSubmit();
      } catch (e: any) {
        handleError(e);
      }
    }
  };

  const isPasswordFormValid =
    !(errors.code || errors.password || errors.passwordRepeat) &&
    getValues('code') &&
    getValues('password') &&
    getValues('passwordRepeat');

  useEffect(() => {
    if (pathname.includes(routes.auddlySignUpPassword.path)) {
      setFormStep(AuddlySignUpFormStep.PASSWORD_CREATION);
    }
    if (pathname.includes(routes.auddlySignUpLocation.path)) {
      setFormStep(
        isPasswordFormValid
          ? AuddlySignUpFormStep.LOCATION_DETAILS
          : AuddlySignUpFormStep.PASSWORD_CREATION
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="auddly-signup-form">
        <AuddlySignUpFormStepIndicator step={formStep} />
        {formStep === AuddlySignUpFormStep.PASSWORD_CREATION && (
          <AuddlySignUpPasswordForm
            passwordPolicy={passwordPolicy}
            showEmailField={!email}
            codeErrorMessage={codeErrorMessage}
          />
        )}
        {formStep === AuddlySignUpFormStep.LOCATION_DETAILS && (
          <AuddlySignUpLocationForm countries={countryOptions} />
        )}
        <AuddlySignUpActions
          isSubmitting={isSubmitting}
          submitEnabled={isDirty && isValid && Boolean(signUpLink)}
          step={formStep}
          onNext={() =>
            navigate({ pathname: routes.auddlySignUpLocation.path, search })
          }
          disableNextStep={!isPasswordFormValid}
          email={(email as string) || watchEmail}
          resendLink={resendLink?.href}
        />
      </form>
    </FormProvider>
  );
};
